// TODO: these are shared types used in webmate-web and webmate-reportviewer.
//    They should be moved to a shared package. (Currently they are manually copied to both projects (what could go wrong?)
// Version: 1.2024-08-10

import {ScreenshotId} from "@webmate-web-core/shared/types/ids";

export type BlobId = string;
export type ProjectId = string;
export type UserId = string;
export type ReportId = string;
export type DateTime = string;

export interface ConstHTMLReportExpr {
    type: 'ConstHTMLStringReportExpr';
    value: string;
}

export interface ImageReportExpr {
    type: 'ImageReportExpr';
    imageId: ScreenshotId;
}

export interface EnumReportExpr {
    type: 'EnumReportExpr';
    options: string[];
    value: string;
}

export type ReportExpr = ConstHTMLReportExpr | ImageReportExpr | EnumReportExpr;

export interface ReportOutput {
    filename?: string;
    blobId?: BlobId;
    optError?: string;
    state: 'pending' | 'ready' | 'failed';
    lastUpdate: DateTime;
}

export interface Report {
    id: ReportId;
    projectId: ProjectId;
    title: string;
    description: string;
    sections: ReportSection[];
    sectionContent: {[sectionId: string]: ReportSectionContent[]};
    renderConfig: ReportRenderConfig;
    creator: UserId;
    creationTime: DateTime;
    version: number;
    lastUpdateTime: DateTime;
    lastUpdater: UserId;
    validUntil?: DateTime;
    outputs: {[outputType: string]: ReportOutput};
}

export interface RenderingProfile {
    name: string;
    parent?: string;
    styling: { cssText: string };
}

export type SectionNumberingScheme = 'HierarchicalNumbering' | 'SimpleNumbering' | 'NoNumbering';

export const DefaultNumberingScheme: SectionNumberingScheme = 'HierarchicalNumbering';

export interface ReportRenderConfig {
    renderingProfiles: { [name: string]: RenderingProfile };
    numberingScheme: SectionNumberingScheme;
    language: string;
}

/**
 * Those positions are used to determine the layout of the report section content.
 * - main: normal in-flow content
 * - aside: content that should be displayed in the sidebar (or similar depending on the rendering target)
 * - panel: content that should be displayed in a panel
 */
export type ReportSectionContentPosition = 'main' | 'aside' | 'panel';

export interface ImageContent {
    type: 'ImageContent';
    position?: ReportSectionContentPosition;
    imageURLOrBlob: { url: string } | { blobId: BlobId };
    imageAltText?: string;
    caption?: string;
}

export interface TextContent {
    type: 'TextContent';
    position?: ReportSectionContentPosition;
    text: string;
}

export interface DonutData {
    type: 'DonutChartData';
    data: { label: string, value: number }[];
}

export type ChartData = DonutData;

export interface ChartContent {
    type: 'ChartContent';
    position?: ReportSectionContentPosition;
    chartData: ChartData;
}

export interface TableCell {
    content: string;
    isHeader?: boolean;
}

export interface TableContent {
    type: 'TableContent';
    position?: ReportSectionContentPosition;
    rows: TableCell[][];
}

/**
 * Whether the items in a content container should be displayed with its original width or if they should stretch to fill the container.
 * Default is auto.
 */
export type ContentContainerItemLayout = 'auto' | 'stretch';

/**
 * A container for content. This is used to group content together, e.g. to display it side-by-side in a panel.
 */
export interface ContentContainer {
    type: 'ContentContainer';
    position?: ReportSectionContentPosition;
    children: ReportSectionContent[];
    childLayout?: ContentContainerItemLayout[]; // layout for each child, index corresponds to the index in children
    caption?: string;
}

export type ReportSectionId = string;

export interface HierarchicalNumbering {
    type: 'HierarchicalNumbering';
    numbering: string;
}

export interface SimpleNumbering {
    type: 'SimpleNumbering';
    numbering: string;
}

export interface NoNumbering {
    type: 'NoNumbering';
}

export type SectionNumbering = HierarchicalNumbering | SimpleNumbering | NoNumbering;

export interface ReportSection {
    id: ReportSectionId;
    customId?: string; // can be used to modify styling and to link to sections
    title?: string;
    titlePrefix?: string;
    titleSuffix?: string;
    subTitle?: string;
    subsections: ReportSection[];
    numberingOverride?: SectionNumbering;
    styleClasses?: string[];
    hideInToC?: boolean;
}

export type ReportSectionContent = ImageContent | TextContent | TableContent | ChartContent | ContentContainer;
